/**
 * §A2's roster band — the heading above each of the two roster panels, and its count line.
 *
 * `04-officials-profiles.md` §A2 draws two of them:
 *
 * > `<h2>Technical Panel <span class="md">· ATDs, Technical Officers And Judges</span></h2>`
 * > · count `22 officials · 2 ATDs · 6 TOs · 14 Judges` (numbers bold)
 *
 * **Every figure is counted from the rows the panel below actually holds.** §A2 prints the
 * demonstration roster's numbers, and Q11's note on the README contradiction is explicit
 * that *"every denominator is computed from the loaded roster and never hard-coded"* — so
 * `22`, `2`, `6` and `14` are what this component would print for that roster and not
 * constants it prints regardless. A band that said `22 officials` over nineteen rows would
 * be the one thing a people index must never do.
 *
 * **Why the role order is passed in and not derived.** §A2 fixes it — ATD, TO, Judge on the
 * technical band, UM then Umpire on the umpiring one — and it is the same list that orders
 * the panel's role tabs, so the two are handed the same array by the page rather than each
 * choosing an order of its own.
 */

import type { OfficialRole } from "@hockeytd/shared";

import type { ReportProgressRow } from "../../api/types.js";

/**
 * The plural §A2 prints beside each figure. Two of the five are irregular enough that a
 * bare `+ "s"` would be wrong on a screen the TD reads at a glance (`Judges`, `Umpires` are
 * fine; `TOs` and `UMs` pluralise an abbreviation, and `Tournament Administrators` would be
 * a mouthful if it ever reached a band), so the map is written out.
 */
const ROLE_PLURAL: Readonly<Record<OfficialRole, string>> = {
  TD: "TDs",
  ATD: "ATDs",
  TO: "TOs",
  Judge: "Judges",
  UM: "UMs",
  Umpire: "Umpires",
  "Tournament Administrator": "Tournament Administrators",
};

const ROLE_SINGULAR: Readonly<Record<OfficialRole, string>> = {
  TD: "TD",
  ATD: "ATD",
  TO: "TO",
  Judge: "Judge",
  UM: "UM",
  Umpire: "Umpire",
  "Tournament Administrator": "Tournament Administrator",
};

export interface RosterCountPart {
  count: number;
  label: string;
}

/**
 * §A2's count line, as parts: the panel total first, then one part per role in the order
 * the band lists them.
 *
 * A role with nobody in it still gets a part — `0 Judges` is a fact about this tournament's
 * panel, and dropping it would leave the TD to notice an absence rather than read it.
 */
export function rosterCountParts(
  officials: readonly ReportProgressRow[],
  roles: readonly OfficialRole[],
): RosterCountPart[] {
  const parts: RosterCountPart[] = [
    { count: officials.length, label: officials.length === 1 ? "official" : "officials" },
  ];
  for (const role of roles) {
    const count = officials.filter((official) => official.role === role).length;
    parts.push({ count, label: count === 1 ? ROLE_SINGULAR[role] : ROLE_PLURAL[role] });
  }
  return parts;
}

export interface RosterBandProps {
  /** Used for the band's testid, so the two bands on the page cannot be confused. */
  id: string;
  /** `Technical Panel` / `Umpiring Panel`. */
  title: string;
  /** §A2's thin half, without its leading separator: `ATDs, Technical Officers And Judges`. */
  subtitle: string;
  officials: readonly ReportProgressRow[];
  roles: readonly OfficialRole[];
}

export function RosterBand({
  id,
  title,
  subtitle,
  officials,
  roles,
}: RosterBandProps): React.JSX.Element {
  const parts = rosterCountParts(officials, roles);

  return (
    <div className="repband repband--sub" data-testid={`${id}Band`} data-howto={`${id}-band`}>
      <h2>
        {title} <span className="md">· {subtitle}</span>
      </h2>
      <span className="spacer" />
      <p className="rosterband__count" data-testid={`${id}BandCount`}>
        {parts.map((part, index) => (
          <span key={part.label}>
            {index > 0 ? <span aria-hidden="true"> · </span> : null}
            <b>{part.count}</b> {part.label}
          </span>
        ))}
      </p>
    </div>
  );
}
